import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Home, User, Briefcase, LogOut, ChevronDown, ClipboardList, MessageCircle, CalendarCheck } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'
import { BrandMark, BRAND_NAME } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'
import { hasCompany, setMode } from '@/lib/account'
import { cn } from '@/lib/utils'
import { unreadTotal, useThreads } from '@/lib/messages'

/**
 * The client side of MySerbisyo (spec 002). Phone first: a top bar and a bottom navigation at
 * phone width, the same content centred on a wider screen. Nothing here reaches business data:
 * a client account has no company, so every business table returns no row to it anyway.
 */
export const Route = createFileRoute('/_client')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login' })
    const { data: p } = await supabase.from('profile').select('display_name').eq('id', data.session.user.id).maybeSingle()
    if (!p?.display_name) throw redirect({ to: '/welcome' })
  },
  component: ClientLayout,
})

const NAV = [
  { to: '/home', icon: Home, key: 'client.nav.home' },
  { to: '/tasks', icon: ClipboardList, key: 'tasks.nav' },
  { to: '/bookings', icon: CalendarCheck, key: 'bk.nav' },
  { to: '/messages', icon: MessageCircle, key: 'msg.nav' },
  { to: '/profile', icon: User, key: 'client.nav.profile' },
] as const

function ClientLayout() {
  const { t } = useT()
  const { session, signOut } = useAuth()
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  const isProvider = hasCompany(session)
  const unread = unreadTotal(useThreads('client').data)

  const toBusiness = async () => {
    setMenu(false)
    if (!isProvider) return navigate({ to: '/offer-services' })
    await setMode('business')
    navigate({ to: '/dashboard' })
  }

  return (
    <div className="min-h-screen bg-canvas pb-[calc(64px+env(safe-area-inset-bottom,0px))] sm:pb-0">
      <header className="sticky top-0 z-20 border-b border-token-subtle bg-token-surface/95 pt-[env(safe-area-inset-top,0px)] backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <Link to="/home" className="flex items-center gap-2">
            <BrandMark className="h-8 w-8" />
            <span className="font-display text-[17px] font-bold tracking-[-0.02em] text-token-primary">{BRAND_NAME}</span>
          </Link>
          <nav className="ml-6 hidden gap-1 sm:flex">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="rounded-[9px] px-3 py-1.5 text-[13.5px] font-semibold text-token-secondary hover:text-token-primary"
                activeProps={{ className: 'bg-token-selected text-brand' }}>{t(n.key)}{n.to === '/messages' && unread > 0 ? ` (${unread})` : ''}</Link>
            ))}
          </nav>
          <div className="relative ml-auto">
            <button type="button" onClick={() => setMenu((m) => !m)} aria-expanded={menu}
              className="flex items-center gap-1 rounded-[10px] border border-token-subtle px-2.5 py-1.5 text-[13px] font-semibold text-token-primary">
              <Briefcase className="h-4 w-4" /><span className="hidden sm:inline">{isProvider ? t('client.mode.business') : t('client.mode.offer')}</span><ChevronDown className="h-3.5 w-3.5" />
            </button>
            {menu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                <div className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-xl border border-token-subtle bg-token-raised py-1 shadow-lg">
                  <button type="button" onClick={toBusiness} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13.5px] font-semibold text-token-primary hover:bg-token-hover">
                    <Briefcase className="h-4 w-4" /> {isProvider ? t('client.mode.business') : t('client.mode.offer')}
                  </button>
                  <div className="px-3 py-2"><LanguageMenu /></div>
                  <button type="button" onClick={async () => { setMenu(false); await signOut(); navigate({ to: '/login' }) }}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13.5px] font-semibold text-token-secondary hover:bg-token-hover">
                    <LogOut className="h-4 w-4" /> {t('client.signOut')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-token-subtle bg-token-surface pb-[env(safe-area-inset-bottom,0px)] sm:hidden">
        <div className="grid grid-cols-5">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="flex h-16 flex-col items-center justify-center gap-0.5 px-0.5 text-center text-[10.5px] font-semibold leading-tight text-token-muted"
              activeProps={{ className: cn('text-brand') }}>
              <span className="relative"><n.icon className="h-5 w-5" />
                {n.to === '/messages' && unread > 0 && <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-token-danger px-1 text-[10px] font-bold text-token-inverse">{unread}</span>}
              </span>{t(n.key)}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  )
}
